import { Box, Typography, useTheme, Link } from '@mui/material';
import { usePost } from '../../services/apiService/useApiRequest';
import API_ENDPOINTS from '../../consts/endpoint';
import { useNavigate } from 'react-router-dom';
import { FormEvent, useContext, useEffect, useState } from 'react';
import {
  API_RESPONSE_STATUS_CODE,
  BREAKPOINTS,
  DEFAULT_OTP_LENGTH,
  isMobileNumberInputValid,
  LOCAL_STORAGE_KEYS,
  NAVIGATION_CONSTANTS,
  OTP_TIMER_IN_SECONDS,
  USER_TYPE,
} from '../../consts/constants';
import { AuthContext } from '../../globalContext/auth/authProvider';
import { RouteContext } from '../../globalContext/routes/routeProvider';
import { setAuth } from '../../globalContext/auth/authActions';
import { UserContext } from '../../globalContext/user/userProvider';
import { setUserInfo } from '../../globalContext/user/userActions';
import { UserState } from '../../types';
import { Styles } from './LoginStyles';
import Logo from '../../assets/images/sparklLogoWhite.png';
import { useTranslation } from 'react-i18next';
import LoginForm from '../../components/LoginForm';
import PoweredBySparkl from '../../components/PoweredBySparkl';
import CustomButton from '../../components/CustomButton';
import WestIcon from '@mui/icons-material/West';
import OtpBoxes from '../../components/OtpBoxes';
import { useWindowDimension } from '../../hooks/useWindowDimension';

import LoginLeftContainer from '../../components/LoginLeftContainer';
import {
  getItem,
  removeItem,
  setItem,
} from '../../services/localStorageService';
import { formatTime } from '../../utils/util';
import { ToastContext } from '../../globalContext/toast/toastProvider';
import { showErrorToast } from '../../globalContext/toast/toastActions';
import { Config } from '../../consts/config';
import {
  applyBrandTheme,
  clearLocalHaleSession,
  extractBuIdFromPayload,
  HALE_PRIVACY_URL,
  HALE_TERMS_URL,
  resolveAuthenticatedBrandKey,
  resolveUserBuId,
  setLocalHaleSession,
  shouldShowHaleLoginPage,
} from '../../theme/brands';
import { ThemeContext } from '../../globalContext/theme/themeProvider';
import { toggleColorMode } from '../../globalContext/theme/themeActions';
import HaleLoginLeftPanel from './components/HaleLoginLeftPanel';
import HaleLogo from './components/HaleLogo';
import { HaleLoginStyles as haleStyles } from './HaleLoginStyles';





const Login: React.FC = () => {
  const [, authDispatch] = useContext(AuthContext);
  const [, dispatchUserInfo] = useContext(UserContext);
  const [, themeDispatch] = useContext(ThemeContext);
  const [routeState] = useContext(RouteContext);
  const [, dispatchToastInfo] = useContext(ToastContext);

  const [currentStep, setCurrentStep] = useState(1);
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [phoneNo, setPhoneNo] = useState('');
  const [countryIsd, setCountryIsd] = useState(() =>
    shouldShowHaleLoginPage() ? '+971' : '+91',
  );
  const [otp, setOtp] = useState('');
  const [isOtpValid, setIsOtpValid] = useState(false);

  const [time, setTime] = useState(0);
  const [resendOtp, setResendOtp] = useState(false);

  useEffect(() => {
    const initializeWebEngage = () => {
      if (typeof window !== 'undefined' && window.webengage) {
        try {
          const { hostname } = window.location;
          if (hostname.includes('uat-')) {
            return false;
          }
          const licenseCode = hostname.includes('.me') ? '76aba82' : '11b565a79';
          window.webengage.init(licenseCode);
          return true;
        } catch (error) {
          console.error('WebEngage initialization error:', error);
          return false;
        }
      }
      return false;
    };

    let attempt = 0;
    const maxAttempts = 3;
    const retryInterval = 2000;
    const tryInitialize = () => {
      if (attempt >= maxAttempts) {
        console.warn('Max WebEngage initialization attempts reached');
        return;
      }
      attempt++;
      if (initializeWebEngage()) return;
      else if (attempt < maxAttempts) {
        setTimeout(tryInitialize, retryInterval);
      }
    };
    tryInitialize();
  }, []);

  const showHaleLogin = shouldShowHaleLoginPage();

  useEffect(() => {
    if (showHaleLogin) {
      setLocalHaleSession();
    } else {
      clearLocalHaleSession();
    }
  }, [showHaleLogin]);

  useEffect(() => {
    const brandKey = showHaleLogin ? 'hale' : 'student';
    themeDispatch(toggleColorMode({ brand: applyBrandTheme(brandKey) }));
  }, [showHaleLogin, themeDispatch]);

  const navigate = useNavigate();
  const theme = useTheme();
  const styles = Styles(theme);
  const { t } = useTranslation();
  const [width] = useWindowDimension();
  const isMd = width <= BREAKPOINTS.md;
  const isXs = width <= BREAKPOINTS.xs;

  const { isLoading: isOtpVerified, makeRequest: login } = usePost({
    url: API_ENDPOINTS.LOGIN,
    apiOptions: {
      headers: {
        'Content-Type': 'application/json',
        'user-type': USER_TYPE.STUDENT,
        'X-User-Type': USER_TYPE.STUDENT,
      },
    },
    otherOptions: { isForNonLoggedInUser: true },
  });

  const { isLoading: isOtpSending, makeRequest: sendOtp } = usePost({
    url: API_ENDPOINTS.SEND_OTP,
    apiOptions: {
      headers: {
        'Content-Type': 'application/json',
        'user-type': USER_TYPE.STUDENT,
        'X-User-Type': USER_TYPE.STUDENT,
      },
    },
    otherOptions: { isForNonLoggedInUser: true },
  });

  const loginHandler = async (otpValue = '') => {
    const payload = {
      otp: otpValue.length > 0 ? otpValue : otp,
      mobile: phoneNo,
      isd: countryIsd,
    };

    // Construct base URL without /api/v1
    const baseUrl = Config.BACKEND_DOMAIN || process.env.REACT_APP_API_URL?.replace('/api/', '') || 'https://api.sparkl.ac';
    const loginUrl = `${baseUrl}/auth/v1/login`;

    await login({
      overrideUrl: loginUrl,
      body: payload,
      queryParamsObject: {},
      onErrorCallback: (error: any) => {
        const message =
          (typeof error === 'string' && error) ||
          error?.message ||
          t('login.otpIncorrect');
        dispatchToastInfo(
          showErrorToast({
            message,
          }),
        );
      },
      onSuccessCallback: (response: any) => {
        if (response) {
          const responseStatus = response?.status ?? response?.data?.status;
          if (
            responseStatus != null &&
            responseStatus !== API_RESPONSE_STATUS_CODE.SUCCESS
          ) {
            const message = response?.message || t('login.otpIncorrect');
            dispatchToastInfo(
              showErrorToast({
                message,
              }),
            );
            return;
          }

          // Response structure: { status: 1, message: 'Login successful', data: { access_token, refresh_token, user_id, user_type, ... } }
          // useApiRequest passes response.data (axios response.data) to onSuccessCallback
          const responseData = response?.data || response;
          
          // Extract tokens from response.data
          const accessToken = responseData?.access_token || responseData?.accessToken;
          const refreshToken = responseData?.refresh_token || responseData?.refreshToken;
          
          // Ensure we have at least one token
          if (!accessToken && !refreshToken) {
            console.error('No tokens found in response. Full response:', response);
            dispatchToastInfo(
              showErrorToast({
                message: 'Login failed: No tokens received',
              }),
            );
            return;
          }
          
          if (showHaleLogin) {
            setLocalHaleSession();
          } else {
            clearLocalHaleSession();
          }

          const userBuId = resolveUserBuId(extractBuIdFromPayload(responseData), {
            fromHaleContext: showHaleLogin,
          });
          setItem(LOCAL_STORAGE_KEYS.USER_BU_ID, String(userBuId));

          const userId = responseData?.user_id;
          const userType = responseData?.user_type || USER_TYPE.STUDENT;
          const userData = responseData?.user || responseData;
          const userImage = userData?.image || userData?.profile_picture || userData?.avatar || '';

          if (userId) {
            try {
              const {hostname} = window.location
              if (window.webengage && !hostname.includes("uat-")) {
                window.webengage.user?.login(userId);
              } else {
                console.warn('WebEngage not initialized, skipping tracking');
              }
            } catch (error) {
              console.error('WebEngage tracking error:', error);
            }

            dispatchUserInfo(setUserInfo({
              firstName: userData?.name?.split(' ')[0] || '',
              lastName: userData?.name?.split(' ')[1] || '',
              email: userData?.email || '',
              id: `${userId}`,
              userType: userType,
              profileImg: userImage,
              grade: userData?.grade || '',
              gender: userData?.gender !== undefined ? userData.gender : undefined,
              profileCompletion: userData?.profile_completion || 0,
            }));
          }

          // Set tokens after buId/user are stored
          authDispatch(setAuth({ 
            token: refreshToken || accessToken, // Keep token for backward compatibility (AUTH_KEY)
            accessToken: accessToken,
            refreshToken: refreshToken,
          }));

          const brandConfig = applyBrandTheme(resolveAuthenticatedBrandKey());

          themeDispatch(
            toggleColorMode({
              brand: brandConfig,
            }),
          );

          removeItem(LOCAL_STORAGE_KEYS.OTP_ATTEMPTS_LEFT);
          removeItem(LOCAL_STORAGE_KEYS.OTP_SENT_AT);
          if (!localStorage.getItem(LOCAL_STORAGE_KEYS.IS_ONBOARDING)) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.IS_ONBOARDING, "0");
          }
          
          // Small delay to ensure state and localStorage are updated before navigation
          setTimeout(() => {
            if (routeState?.route) {
              navigate(routeState?.route);
              return;
            }
            navigate(`${NAVIGATION_CONSTANTS.DASHBOARD}`);
          }, 100);
        }
      },
    });
  };
  const sendOtpHandler = async (isResend = false) => {
    const payload = {
      mobile: phoneNo,
      isd: countryIsd,
      action: 'login',
      app_signature: 'web',
    };

    // Construct base URL without /api/v1
    const baseUrl = Config.BACKEND_DOMAIN || process.env.REACT_APP_API_URL?.replace('/api/', '') || 'https://api.sparkl.ac';
    const sendOtpUrl = `${baseUrl}/auth/v1/signin`;

    await sendOtp({
      overrideUrl: sendOtpUrl,
      body: payload,
      onSuccessCallback: (response: any) => {
        // Handle different response structures
        // response is response.data from axios (the actual response body)
        // Since we're in success callback, the HTTP request was successful (200/201)
        // Proceed regardless of response structure
        const responseData = response?.data || response;
        const otpAttemptsLeft = responseData?.retry_count || responseData?.retryCount || response?.retry_count || response?.retryCount || 3;
        let otpTimerInSeconds = OTP_TIMER_IN_SECONDS.RESEND;

        setItem(LOCAL_STORAGE_KEYS.OTP_ATTEMPTS_LEFT, `${otpAttemptsLeft}`);
        if (otpAttemptsLeft === 0) {
          otpTimerInSeconds = OTP_TIMER_IN_SECONDS.BLOCK_USER;
        }
        const resendOtpAllowAfter = new Date().setSeconds(
          new Date().getSeconds() + otpTimerInSeconds,
        );
        setItem(
          LOCAL_STORAGE_KEYS.OTP_SENT_AT,
          new Date(resendOtpAllowAfter).toISOString(),
        );
        setTime(otpTimerInSeconds);

        if (!isResend) setCurrentStep(2);
      },
      onErrorCallback: (error: any) => {
        dispatchToastInfo(
          showErrorToast({
            message: error?.message || error || 'Something went wrong! Please try again later',
          }),
        );
      },
    });
  };

  const submitHandler = (e: FormEvent) => {
    e.preventDefault();
    if (currentStep === 1) {
      setOtp('');
      sendOtpHandler();
      return;
    }

    if (otp && otp.length === DEFAULT_OTP_LENGTH) loginHandler();
  };

  const isPhoneValid = (value: string) => {
    const isValid = isMobileNumberInputValid(value);
    setIsPhoneNumberValid(isValid);
    return isValid;
  };

  useEffect(() => {
    const lastOtpSentAt = getItem(LOCAL_STORAGE_KEYS.OTP_SENT_AT);
    const timeDiffInSeconds = Math.floor(
      (new Date(lastOtpSentAt).getTime() - new Date().getTime()) / 1000,
    );
    if (timeDiffInSeconds > 0) {
      setTime(timeDiffInSeconds);
    } else {
      setTime(0);
    }
  }, []);

  useEffect(() => {
    if (time === 0) {
      setResendOtp(true);
      return;
    } else {
      setResendOtp(false);
    }

    const interval = setInterval(() => {
      setTime((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  const goBackToPhoneStep = () => {
    setCurrentStep(1);
    if (phoneNo) {
      isPhoneValid(phoneNo);
    }
  };

  const BackBtn = ({ showText = true, style = {} }) => {
    return (
      <Box
        sx={{ ...styles.back, position: 'absolute', ...style }}
        onClick={() => {
          setCurrentStep(1);
        }}
      >
        <WestIcon />
        {showText && t('login.back')}
      </Box>
    );
  };

 

  if (showHaleLogin) {
    return (
      <>
      <Box id="login-page" sx={haleStyles.container}>
        {!isMd && <HaleLoginLeftPanel />}
        <Box sx={haleStyles.rightPanel}>
          <Box sx={haleStyles.rightContent}>
            {!isXs && currentStep === 2 && (
              <Box sx={haleStyles.back} onClick={goBackToPhoneStep}>
                <WestIcon fontSize="small" />
                {t('login.back')}
              </Box>
            )}

            {isXs && currentStep === 2 && (
              <Box
                sx={{ ...haleStyles.back, position: 'absolute', top: 0, left: 0 }}
                onClick={goBackToPhoneStep}
              >
                <WestIcon fontSize="small" />
              </Box>
            )}

            <HaleLogo />

            <Box>
              <Typography sx={haleStyles.heading}>
                {currentStep === 1 ? (
                  <>
                    Welcome to
                    <br />
                    Hale Education
                  </>
                ) : (
                  t('login.otpHeading')
                )}
              </Typography>
              {currentStep === 1 ? (
                <Typography sx={haleStyles.subHeading}>
                  Let&apos;s continue your learning journey...
                </Typography>
              ) : (
                <Typography sx={haleStyles.otpDesc}>
                  {t('login.otpDesc')}{' '}
                  <Box component="span" sx={haleStyles.phoneNo}>
                    {countryIsd} {phoneNo}.
                    <Box
                      component="span"
                      sx={haleStyles.changePhoneNo}
                      onClick={goBackToPhoneStep}
                    >
                      {t('login.change')}
                    </Box>
                  </Box>
                </Typography>
              )}
            </Box>

            <Box>
              <form onSubmit={submitHandler}>
                <Box sx={haleStyles.formContainer}>
                  <Box sx={{ display: currentStep === 1 ? 'block' : 'none' }}>
                    <LoginForm
                      variant="hale"
                      value={phoneNo}
                      isPhoneValid={isPhoneNumberValid}
                      onCountryCodeChange={setCountryIsd}
                      onChange={(value: string) => {
                        setPhoneNo(value);
                        isPhoneValid(value);
                      }}
                    />
                  </Box>
                  {currentStep === 2 && (
                    <Box sx={haleStyles.otpContainer}>
                      <OtpBoxes
                        variant="hale"
                        otp={otp}
                        onChange={(value: string) => {
                          setOtp(value);
                          if (value.length === DEFAULT_OTP_LENGTH) {
                            setIsOtpValid(true);
                            loginHandler(value);
                          } else {
                            setIsOtpValid(false);
                          }
                        }}
                      />
                      <Typography
                        sx={{
                          ...haleStyles.resendCode,
                          ...(resendOtp ? haleStyles.resendCodeActive : {}),
                          cursor: resendOtp ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => {
                          if (!resendOtp) return;
                          sendOtpHandler(true);
                        }}
                      >
                        {t('login.resendCode')}{' '}
                        <Box component="span" sx={haleStyles.otpTimer}>
                          {formatTime(time)}
                        </Box>
                      </Typography>
                    </Box>
                  )}
                </Box>

                <CustomButton
                  type="submit"
                  sx={haleStyles.submitBtn}
                  isDisabled={
                    (currentStep === 1 && !isPhoneNumberValid) ||
                    (currentStep === 2 && !isOtpValid)
                  }
                  onClick={() => {}}
                  showLoader={isOtpSending || isOtpVerified}
                >
                  <span>
                    {currentStep === 1 ? t('login.next') : t('login.login')}
                  </span>
                </CustomButton>

                {currentStep === 1 && (
                  <Typography component="p" sx={haleStyles.termsText}>
                    By clicking <strong>Next</strong>, you agree to Hale
                    Education&apos;s{' '}
                    <Link
                      href={HALE_TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={haleStyles.termsLink}
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      href={HALE_PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={haleStyles.termsLink}
                    >
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                )}
              </form>
            </Box>
          </Box>
          <Box sx={haleStyles.poweredByRow}>
            <PoweredBySparkl />
          </Box>
        </Box>
      </Box>
      </>
    );
  }

  return (
    <>
      <Box id="login-page" sx={styles.container}>
        {isXs && <Box sx={styles.dummy}></Box>}
        {!isMd && (
          <Box sx={styles.leftContainer}>
            <LoginLeftContainer />
          </Box>
        )}
        <Box
          sx={{
            ...styles.rightContainer,
            ...(isMd && styles.fullWidthContainer),
          }}
        >
          {(!isXs || (isXs && currentStep === 1)) && (
            <Box sx={styles.imgContainer}>
              <img src={Logo} style={styles.img} alt="logo" />
            </Box>
          )}

          {isXs && (
            <Box sx={styles.emptyContainer}>
              {currentStep === 2 && (
                <BackBtn
                  showText={false}
                  style={{
                    top: theme.custom.gap.medium,
                  }}
                />
              )}
            </Box>
          )}
          <Box sx={{ ...styles.loginFormContainer, position: 'relative' }}>
            {!isXs && currentStep === 2 && <BackBtn />}

            <Box>
              <div style={{ ...styles.formHeading }}>
                {currentStep === 1
                  ? `${t('login.heading')}`
                  : t('login.otpHeading')}
                {currentStep === 1 ? (
                  <p style={{ position: 'relative' }}>
                    {t('login.subHeading')}{' '}
                    <span style={{ ...styles.emoji, position: 'absolute' }}>
                      &#128588;
                    </span>
                  </p>
                ) : (
                  <p>{t('login.otpSubHeading')} </p>
                )}
              </div>

              <p style={styles.formDesc}>
                {currentStep === 1 ? (
                  t('login.desc')
                ) : (
                  <>
                    {t('login.otpDesc')}{' '}
                    <span style={styles.phoneNo}>
                      {countryIsd} {phoneNo}.
                      <span
                        style={styles.changePhoneNo}
                        onClick={() => setCurrentStep(1)}
                      >
                        {t('login.change')}
                      </span>
                    </span>
                  </>
                )}
              </p>

            </Box>
            <Box>
              <form
                onSubmit={(e) => {
                  submitHandler(e);
                }}
              >
                <Box sx={styles.formContainer}>
                  {currentStep === 1 ? (
                    <LoginForm
                      value={phoneNo}
                      isPhoneValid={isPhoneNumberValid}
                      onCountryCodeChange={setCountryIsd}
                      onChange={(value: string) => {
                        setPhoneNo(value);
                        isPhoneValid(value);
                      }}
                    />
                  ) : (
                    <Box sx={styles.otpContainer}>
                      <OtpBoxes
                        otp={otp}
                        onChange={(value: string) => {
                          setOtp(value);
                          if (value.length === DEFAULT_OTP_LENGTH) {
                            setIsOtpValid(true);
                            loginHandler(value);
                          } else {
                            setIsOtpValid(false);
                          }
                        }}
                      />
                      <Typography
                        sx={{
                          ...styles.resendCode,
                          ...{ cursor: resendOtp ? 'pointer' : 'not-allowed' },
                        }}
                        onClick={() => {
                          if (!resendOtp) return;
                          sendOtpHandler(true);
                        }}
                      >
                        {t('login.resendCode')}{' '}
                        <span style={styles.otpTimer}>{formatTime(time)}</span>
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box>
                  <CustomButton
                    type="submit"
                    isYellowButton={true}
                    sx={styles.submitBtn}
                    isDisabled={
                      (currentStep === 1 && !isPhoneNumberValid) ||
                      (currentStep === 2 && !isOtpValid)
                    }
                    onClick={() => { }}
                    showLoader={isOtpSending || isOtpVerified}
                  >
                    <span>
                      {currentStep === 1 ? t('login.next') : t('login.login')}
                    </span>
                  </CustomButton>
                  {currentStep === 1 && (
                    <Typography
                      component="p"
                      sx={{
                        textAlign: 'center',
                        marginTop: '12px',
                        fontSize: '12px',
                        color: '#718096',
                      }}
                    >
                      By clicking <strong>Next</strong>, you agree to Sparkl's{' '}
                      <Link
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                        }}
                      >
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link
                        href="/policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          fontWeight: 'bold',
                        }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </Typography>
                  )}
                </Box>
              </form>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Login;
